import React, { useRef } from 'react'
import { toNumber } from 'lodash-es'
import { FormState, FieldState } from 'formstate'
import { Trans } from '@lingui/react'
import { observer, useLocalStore } from 'mobx-react-lite'
import { Grid, GridCol, Input, Icon, ButtonLayout, ButtonPrimary } from '../../../ui-kit'
import { ButtonDropdown } from '../../../ui-kit/button/ButtonDropdown'
import { i18n } from '../../../utils/intl'
import { fieldToNumberInputProps } from '../../../utils/form/fieldToInputProps'
import { onlyNumeric, required } from '../../../utils/forms'
import { formatMoney, formatFieldToFixedTwo } from '../../../utils/format'

export interface PriceFilterProps {
  onPriceChange?: (args?: { from: number; to: number }) => void
}

export const PriceFilter = observer(({ onPriceChange = () => ({}) }: PriceFilterProps) => {
  const priceFromRef = useRef<HTMLInputElement | null>(null)
  const priceToRef = useRef<HTMLInputElement | null>(null)
  const toggleRef = useRef<any>(null)

  const state = useLocalStore(() => {
    const fields = {
      from: new FieldState('0').validators(required, onlyNumeric),
      to: new FieldState('1000').validators(required, onlyNumeric),
    }
    const form = new FormState(fields)

    return {
      title: i18n.t`Všetky sumy`,
      fields,
      form,
    }
  })

  return (
    <ButtonDropdown
      type="link"
      size="small"
      space="condensed"
      data-testid="filter-prices"
      content={state.title}
      icon
      iconSize="medium"
      dropdownInteractive
      popperOptions={{
        placement: 'bottom-end',
      }}
      getDropdownStaticRef={ref => {
        toggleRef.current = ref
      }}>
      <Grid>
        <GridCol size={{ m: 12 }}>
          <Input
            inputRef={priceFromRef}
            id="price-from"
            label={i18n.t`Suma od`}
            type="number"
            step="0.01"
            onBlur={() => {
              formatFieldToFixedTwo(state.fields.from)
            }}
            {...fieldToNumberInputProps(state.fields.from)}
            data-testid="amount-from"
            rightAddons={<Icon name="euro" size="medium" className="text-color-grey" />}
          />
        </GridCol>
      </Grid>
      <Grid>
        <GridCol size={{ m: 12 }}>
          <Input
            inputRef={priceToRef}
            id="price-to"
            label={i18n.t`Suma do`}
            {...fieldToNumberInputProps(state.fields.to)}
            type="number"
            step="0.01"
            onBlur={() => {
              formatFieldToFixedTwo(state.fields.to)
            }}
            data-testid="amount-to"
            rightAddons={<Icon name="euro" size="medium" className="text-color-grey" />}
          />
        </GridCol>
      </Grid>
      <Grid>
        <GridCol size={{ m: 12 }}>
          <ButtonLayout className="mb-none">
            <ButtonPrimary
              className="text-nowrap"
              data-testid="submit"
              onClick={async _ => {
                const { form, fields } = state
                await form.validate()

                if (form.hasError) {
                  console.warn(form.error)
                  return
                }

                const from = toNumber(fields.from.$)
                const to = toNumber(fields.to.$)
                state.title = `${formatMoney(from)} - ${formatMoney(to)}`
                // state.title = `${from} - ${to}`
                // close dropdown by simulating button click
                toggleRef.current.trigger.click()
                onPriceChange({ from, to })
              }}>
              <Trans>Potvrdiť</Trans>
            </ButtonPrimary>
          </ButtonLayout>
        </GridCol>
      </Grid>
    </ButtonDropdown>
  )
})

/**
 * Returns function used to filter `data` based on place `type` of selected object's property (`prop`)
 */
export const createPriceFilter = (from: number, to: number, prop: string) => (data: any[]) => {
  return data.filter(i => {
    return i[prop] >= from && i[prop] <= to
  })
}
